import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppShell from '@components/layout/AppShell';
import Placeholder from '@sections/Placeholder';
import ProtectedRoute from '@auth/ProtectedRoute';
import DoctorShell from '@sections/Doctor/DoctorShell';
import { getCurrentUser } from '@api/client';
import { activeRole, roleHome } from '@auth/roles';
/* ════════════════════════════════════════════════════════════════════
   Lazy routes
   Each section is its own chunk. Initial load only fetches the shell
   + whichever section the user lands on.
   ════════════════════════════════════════════════════════════════════ */

const Login          = lazy(() => import('@sections/Login/Login'));
const NotFound = lazy(() => import('@sections/NotFound/NotFound'));
const Legal = lazy(() => import('@sections/Legal/Legal'));

const AuditLog = lazy(() => import('@sections/AuditLog/AuditLog'));
const Clinics  = lazy(() => import('@sections/Clinics/Clinics'));
const Doctors  = lazy(() => import('@sections/Doctors/Doctors'));
const Staff    = lazy(() => import('@sections/Staff/Staff'));
const DoctorForm = lazy(() => import('@sections/Doctors/DoctorForm'));
const StaffForm = lazy(() => import('@sections/Staff/StaffForm'));
const Reports = lazy(() => import('@sections/Reports/Reports'));
const Compliance = lazy(() => import('@sections/Compliance/Compliance'));
const MasterForms = lazy(() => import('@sections/MasterForms/MasterForms'));
const Procedures = lazy(() => import('@sections/Procedures/Procedures'));
const LabReference = lazy(() => import('@sections/LabReference/LabReference'));
const Drugs = lazy(() => import('@sections/Drugs/Drugs'));
const DrugForm = lazy(() => import('@sections/Drugs/DrugForm'));
const DataCorrection = lazy(() => import('@sections/DataCorrection/DataCorrection'));
const Patients = lazy(() => import('@sections/Patients/Patients'));
const PatientDetail = lazy(() => import('@sections/Patients/PatientDetail'));
const PatientEdit = lazy(() => import('@sections/Patients/PatientEdit'));
const Notifications = lazy(() => import('@sections/Notifications/Notifications'));
const Settings = lazy(() => import('@sections/Settings/Settings'));
const ClinicForm = lazy(() => import('@sections/Clinics/ClinicForm'));
const DoctorDashboard = lazy(() => import('@sections/Doctor/Dashboard/Dashboard'));
const SuperAdminDashboard = lazy(() => import('@sections/Dashboard/SuperAdminDashboard'));

/* Role gate: wrong-role visits bounce to '/' where RoleRedirect sends
   them to their own app. Staff creds can no longer open Super Admin. */
function RequireRole({ roles, children }) {
  // Gate on the *active* role, not the raw stored role. For a single-role
  // login the two are identical; for a dual-role login (a doctor who is also
  // this clinic's admin) the active role follows the header switcher, so the
  // Doctor app opens for a clinicadmin who has switched to the Doctor view.
  const active = activeRole(getCurrentUser());
  if (!roles.includes(active)) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const active = activeRole(getCurrentUser());
  if (active === 'doctor') return <Navigate to="/doctor" replace />;
  if (active === 'masseur') return <Navigate to="/desk/masseur" replace />;
  if (active === 'pharmacist') return <Navigate to="/desk/pharmacy" replace />;
  if (active === 'rmo') return <Navigate to="/desk/rmo" replace />;
  if (active === 'assistant') return <Navigate to="/desk/pk-consumables" replace />;
  if (active === 'patient') return <Navigate to="/my" replace />;
  if (active === 'clinicadmin') return <Navigate to="/clinic" replace />;
  if (['reception', 'accountant'].includes(active)) return <Navigate to="/desk" replace />;
  return <Navigate to={roleHome(active)} replace />;
}
const DoctorPatients = lazy(() => import('@sections/Doctor/Patients/Patients'));
const DoctorPatientDetail = lazy(() => import('@sections/Doctor/Patients/PatientDetail'));
const DoctorCalendar = lazy(() => import('@sections/Doctor/Calendar/Calendar'));
const DoctorSchedule = lazy(() => import('@sections/Doctor/Schedule/Schedule'));
const DoctorReports = lazy(() => import('@sections/Doctor/Reports/Reports'));
const DoctorEducation = lazy(() => import('@sections/Doctor/Education/Education'));
const DoctorPromotion = lazy(() => import('@sections/Doctor/Promotion/Promotion'));
const DoctorHelp = lazy(() => import('@sections/Doctor/Help/Help'));
const DoctorBlankPrint = lazy(() => import('@sections/Doctor/Print/BlankPrint'));
const DoctorPkSheet = lazy(() => import('@sections/Doctor/PkSheet/PkSheet'));
const DoctorAccounts = lazy(() => import('@sections/Doctor/Accounts/Accounts'));
const DoctorSettings = lazy(() => import('@sections/Doctor/Settings/Settings'));
const StaffShell = lazy(() => import('@sections/Staff/StaffShell'));
const StaffFrontDesk = lazy(() => import('@sections/Staff/FrontDesk/FrontDesk'));
const StaffAppointments = lazy(() => import('@sections/Staff/Appointments/Appointments'));
const StaffRegistration = lazy(() => import('@sections/Staff/Registration/Registration'));
const StaffQuickAdd = lazy(() => import('@sections/Staff/QuickAdd/QuickAdd'));
const ClinicPanel = lazy(() => import('@sections/ClinicPanel/ClinicPanel'));
const ClinicDoctorForm = lazy(() => import('@sections/ClinicPanel/ClinicDoctorForm'));
const ClinicAdminShell = lazy(() => import('@sections/ClinicPanel/ClinicAdminShell'));
const StaffBilling = lazy(() => import('@sections/Staff/Billing/Billing'));
const StaffPkConsent = lazy(() => import('@sections/Staff/PkConsent/PkConsent'));
const StaffMerge = lazy(() => import('@sections/Staff/Merge/Merge'));
const StaffComplaints = lazy(() => import('@sections/Staff/Complaints/Complaints'));
const StaffMasseur = lazy(() => import('@sections/Staff/Masseur/Masseur'));
const StaffPharmacy = lazy(() => import('@sections/Staff/Pharmacy/Pharmacy'));
const StaffPkConsumables = lazy(() => import('@sections/Staff/PkConsumables/PkConsumables'));
const StaffRmo = lazy(() => import('@sections/Staff/Rmo/Rmo'));
const StaffIncidents = lazy(() => import('@sections/Staff/Incidents/Incidents'));
const PatientShell = lazy(() => import('@sections/Patient/PatientShell'));
const PatientHome = lazy(() => import('@sections/Patient/Home/Home'));
const PatientAppointments = lazy(() => import('@sections/Patient/Appointments/Appointments'));
const PatientRecords = lazy(() => import('@sections/Patient/Records/Records'));
const PatientPkJourney = lazy(() => import('@sections/Patient/PkJourney/PkJourney'));
const PatientBills = lazy(() => import('@sections/Patient/Bills/Bills'));
const PatientProfile = lazy(() => import('@sections/Patient/Profile/Profile'));
const PatientConsent = lazy(() => import('@sections/Patient/Consent/Consent'));
const PatientComplaint = lazy(() => import('@sections/Patient/Complaint/Complaint'));
const PatientFeedback = lazy(() => import('@sections/Patient/Feedback/Feedback'));
const PatientRegistration = lazy(() => import('@sections/Patient/Registration/Registration'));
const DoctorConsultation = lazy(() => import('@sections/Doctor/Consultation/Consultation'));
const DoctorShortConsult = lazy(() => import('@sections/Doctor/ShortConsult/ShortConsult'));
const ConsultationLauncher = lazy(() =>
  import('@sections/Doctor/Consultation/Consultation').then((m) => ({ default: m.ConsultationLauncher }))
);
const CommHub = lazy(() => import('@sections/Comm/CommHub'));
const BookingWizard = lazy(() => import('@sections/PublicBooking/BookingWizard'));
const PublicOnboard = lazy(() => import('@sections/PublicOnboard/PublicOnboard'));
const DoctorDocuments = lazy(() => import('@sections/Doctor/Documents/DoctorDocuments'));
const ClinicDocuments = lazy(() => import('@sections/ClinicPanel/ClinicDocuments'));
const PublicRegister = lazy(() => import('@sections/PublicRegister/PublicRegister'));
const Kyc = lazy(() => import('@sections/Kyc/Kyc'));
const PkDays = lazy(() => import('@sections/Doctor/PkSheet/PkDays'));

function AppFallback() {
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8a8f98', fontSize: 14 }}>Loading…</div>;
}

export default function App() {
  return (
    <Suspense fallback={<AppFallback />}>
    <Routes>
      {/* Public — no auth required */}
      <Route path="/login" element={<Login />} />
      <Route path="/book" element={<BookingWizard />} />
      <Route path="/onboard/:token" element={<PublicOnboard />} />
      <Route path="/register/doctor" element={<PublicRegister kind="doctor" />} />
      <Route path="/register/clinic" element={<PublicRegister kind="clinic" />} />

      {/* Public legal / info pages (linked from the login footer) */}
      <Route path="/privacy"  element={<Legal doc="privacy" />} />
      <Route path="/cookies"  element={<Legal doc="cookies" />} />
      <Route path="/terms"    element={<Legal doc="terms" />} />
      <Route path="/refunds"  element={<Legal doc="refunds" />} />
      <Route path="/contact"  element={<Legal doc="contact" />} />

      {/* Root — dispatch by role */}
      <Route path="/" element={<ProtectedRoute><RoleRedirect /></ProtectedRoute>} />

      {/* ── Super Admin app ── */}
      <Route
        element={
          <ProtectedRoute>
            <RequireRole roles={['superadmin']}>
              <AppShell />
            </RequireRole>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard"        element={<SuperAdminDashboard />} />
        <Route path="/audit"            element={<AuditLog />} />
        <Route path="/clinics"          element={<Clinics />} />
        <Route path="/clinics/new"      element={<ClinicForm />} />
        <Route path="/clinics/:id/edit" element={<ClinicForm />} />
        <Route path="/clinics/:clinicId/panel" element={<ClinicPanel />} />
        <Route path="/clinics/:clinicId/doctors/new" element={<ClinicDoctorForm />} />
        <Route path="/clinics/:clinicId/doctors/:id/edit" element={<ClinicDoctorForm />} />
        <Route path="/doctors"          element={<Doctors />} />
        <Route path="/doctors/new"      element={<DoctorForm />} />
        <Route path="/doctors/:id/edit" element={<DoctorForm />} />
        <Route path="/patients"         element={<Patients />} />
        <Route path="/patients/:id"     element={<PatientDetail />} />
        <Route path="/patients/:id/edit" element={<PatientEdit />} />
        <Route path="/staff"            element={<Staff />} />
        <Route path="/staff/new"        element={<StaffForm />} />
        <Route path="/staff/:id/edit"   element={<StaffForm />} />
        <Route path="/reports"          element={<Reports />} />
        <Route path="/compliance"       element={<Compliance />} />
        <Route path="/forms"            element={<MasterForms />} />
        <Route path="/procedures"       element={<Procedures />} />
        <Route path="/labs"             element={<LabReference />} />
        <Route path="/drugs"            element={<Drugs />} />
        <Route path="/drugs/new"        element={<DrugForm />} />
        <Route path="/drugs/:id/edit"   element={<DrugForm />} />
        <Route path="/data-correction"  element={<DataCorrection />} />
        <Route path="/notifications"    element={<Notifications />} />
        <Route path="/settings"         element={<Settings />} />
        <Route path="/settings/:tab"    element={<Settings />} />
        <Route path="/kyc"              element={<Kyc />} />
      </Route>

      {/* ── Doctor role app ── */}
      <Route
        element={
          <ProtectedRoute>
            <RequireRole roles={['doctor']}>
              <DoctorShell />
            </RequireRole>
          </ProtectedRoute>
        }
      >
        <Route path="/doctor"              element={<DoctorDashboard />} />      
        <Route path="/doctor/patients"     element={<DoctorPatients />} />
        <Route path="/doctor/patients/:id" element={<DoctorPatientDetail />} />
        <Route path="/doctor/calendar" element={<DoctorCalendar />} />
        <Route path="/doctor/schedule" element={<DoctorSchedule />} />
        <Route path="/doctor/reports"  element={<DoctorReports />} />
        <Route path="/doctor/education"  element={<DoctorEducation />} />
        <Route path="/doctor/promotion"  element={<DoctorPromotion />} />
        <Route path="/doctor/help"       element={<DoctorHelp />} />
        <Route path="/doctor/print/:kind" element={<DoctorBlankPrint />} />
        <Route path="/doctor/pk" element={<DoctorPkSheet />} />
        <Route path="/doctor/pk/:courseId" element={<DoctorPkSheet />} />
        <Route path="/doctor/pk/:courseId/days" element={<PkDays />} />
        <Route path="/doctor/accounts" element={<DoctorAccounts />} />
        <Route path="/doctor/settings" element={<DoctorSettings />} />
        <Route path="/doctor/documents" element={<DoctorDocuments />} />
        <Route path="/doctor/notifications" element={<Notifications />} />
        <Route path="/doctor/communication" element={<CommHub />} />
        <Route path="/doctor/consultation"           element={<ConsultationLauncher />} />
    <Route path="/doctor/consultation/:recordId" element={<DoctorConsultation />} />
        <Route path="/doctor/consultation-short/:recordId" element={<DoctorShortConsult />} />
      </Route>

      {/* ── Staff app (Phase 7) — base /desk; SA keeps /staff for staff management ── */}
      <Route
        element={
          <ProtectedRoute>
            <RequireRole roles={['reception', 'accountant', 'masseur', 'pharmacist', 'rmo', 'assistant']}>
              <StaffShell />
            </RequireRole>
          </ProtectedRoute>
        }
      >
        <Route path="/desk" element={<StaffFrontDesk />} />
        <Route path="/desk/appointments" element={<StaffAppointments />} />
        <Route path="/desk/patients/quick" element={<StaffQuickAdd />} />
        <Route path="/desk/patients/new" element={<StaffRegistration />} />
        <Route path="/desk/patients/:uid/edit" element={<StaffRegistration />} />
        <Route path="/desk/billing" element={<StaffBilling />} />
        <Route path="/desk/merge" element={<StaffMerge />} />
        <Route path="/desk/complaints" element={<StaffComplaints />} />
        <Route path="/desk/masseur" element={<StaffMasseur />} />
        <Route path="/desk/pharmacy" element={<StaffPharmacy />} />
        <Route path="/desk/rmo" element={<StaffRmo />} />
        <Route path="/desk/incidents" element={<StaffIncidents />} />
        <Route path="/desk/communication" element={<CommHub />} />
        <Route path="/desk/notifications" element={<Notifications />} />
        <Route path="/desk/pk-consent" element={<StaffPkConsent />} />
        <Route path="/desk/pk-consumables" element={<StaffPkConsumables />} />
      </Route>

      {/* ── Clinic Panel (D22) — clinic admin's own clinic ── */}
      <Route
        element={
          <ProtectedRoute>
            <RequireRole roles={['clinicadmin']}>
              <ClinicAdminShell />
            </RequireRole>
          </ProtectedRoute>
        }
      >
        <Route path="/clinic" element={<ClinicPanel />} />
        <Route path="/clinic/doctors/new" element={<ClinicDoctorForm />} />
        <Route path="/clinic/doctors/:id/edit" element={<ClinicDoctorForm />} />
        <Route path="/clinic/staff/:id/edit" element={<StaffForm />} />
        <Route path="/clinic/patients/quick" element={<StaffQuickAdd />} />
        <Route path="/clinic/patients/new" element={<StaffRegistration />} />
        <Route path="/clinic/patients/:uid/edit" element={<StaffRegistration />} />
        <Route path="/clinic/audit" element={<AuditLog />} />
        <Route path="/clinic/forms" element={<MasterForms />} />
        <Route path="/clinic/procedures" element={<Procedures />} />
        <Route path="/clinic/documents" element={<ClinicDocuments />} />
      </Route>

      {/* ── Patient portal (Phase 8) — base /my ── */}
      <Route
        element={
          <ProtectedRoute>
            <RequireRole roles={['patient']}>
              <PatientShell />
            </RequireRole>
          </ProtectedRoute>
        }
      >
        <Route path="/my" element={<PatientHome />} />
        <Route path="/my/appointments" element={<PatientAppointments />} />
        <Route path="/my/book" element={<PatientAppointments bookMode />} />
        <Route path="/my/records" element={<PatientRecords />} />
        <Route path="/my/records/:recordId" element={<PatientRecords />} />
        <Route path="/my/pk" element={<PatientPkJourney />} />
        <Route path="/my/bills" element={<PatientBills />} />
        <Route path="/my/bills/:invoiceId" element={<PatientBills />} />
        <Route path="/my/profile" element={<PatientProfile />} />
        <Route path="/my/consent" element={<PatientConsent />} />
        <Route path="/my/notifications" element={<Notifications />} />
        <Route path="/my/complaint" element={<PatientComplaint />} />
        <Route path="/my/feedback" element={<PatientFeedback />} />
        <Route path="/my/registration" element={<PatientRegistration />} />
      </Route>

      {/* Catch-all → standalone 404 (no role shell, no auth gate) */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}